import { CheckIcon } from '@/components/common/icons';
import type { ItemModel, ItemType } from '@/types/categories.types';
import type { CSSProperties, FC, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

/** The prototype's type badge colours: background, text. */
const TYPE_COLORS: Record<ItemType, [string, string]> = {
  regex: ['#e6f1f8', '#0b3e6f'],
  // biome-ignore lint/style/useNamingConvention: item type values from the API
  ENUM: ['#e2f5ef', '#0a5c48'],
  // biome-ignore lint/style/useNamingConvention: item type values from the API
  LLM: ['#f2ebfc', '#55309a'],
};

export const typeBadgeStyle = (type: ItemType): CSSProperties => ({
  background: TYPE_COLORS[type][0],
  color: TYPE_COLORS[type][1],
});

/** The small coloured type label ("regex", "LLM", "Číselník"). */
export const TypeBadge: FC<{ type: ItemType; label: string; className?: string }> = ({ type, label, className }) => (
  <span className={`px-1.5 py-0.5 text-[10.5px] font-semibold ${className ?? ''}`.trim()} style={typeBadgeStyle(type)}>
    {label}
  </span>
);

/** Author line of an item or prompt: `default` is the system, the caller's own username is "vy". */
export const authorKind = (author: string, me: string): 'system' | 'me' | 'other' => {
  if (author === 'default') return 'system';
  if (author === me) return 'me';
  return 'other';
};

/** What the table's config button shows: the prompt or regex, or the ENUM values joined by " · ". */
export const configText = (item: Pick<ItemModel, 'type' | 'config' | 'values'>): string =>
  item.type === 'ENUM' ? item.values.join(' · ') : item.config;

/** `Hovorová 12` → `hovorova_12`: the identifier suggested from the description. */
export const slugOf = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');

/** Accent- and case-insensitive text for filters. */
export const normalizeText = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

/**
 * The prototype's dark tooltip: shown while the pointer is over (or focus is in) the wrapper,
 * above it, with a small arrow. `align` picks the side the bubble and arrow hug.
 */
export const Tip: FC<{
  content: ReactNode;
  children: ReactNode;
  align?: 'left' | 'right';
  width?: string;
  className?: string;
  disabled?: boolean;
}> = ({ content, children, align = 'left', width, className = '', disabled = false }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className={`relative ${className}`.trim()}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      {children}
      {open && !disabled ? (
        <div
          role="tooltip"
          className={`pointer-events-none absolute bottom-[calc(100%+8px)] z-30 max-w-[340px] bg-ink px-2.5 py-2 text-[11.5px] font-normal leading-[1.5] text-white shadow-[0_4px_14px_rgba(11,30,50,.18)] ${
            align === 'left' ? 'left-0' : 'right-0'
          }`}
          style={{ width: width ?? 'max-content' }}
        >
          {content}
          <span
            className={`absolute top-full h-0 w-0 border-x-[6px] border-t-[6px] border-x-transparent border-t-ink ${
              align === 'left' ? 'left-3.5' : 'right-2'
            }`}
          />
        </div>
      ) : null}
    </div>
  );
};

/** The 15 px square checkbox of the prototype's menus and lists (decorative; the row is the control). */
export const MenuCheck: FC<{ on: boolean }> = ({ on }) => (
  <span
    className="flex h-[15px] w-[15px] flex-none items-center justify-center rounded-[3px] border-[1.5px]"
    style={{ borderColor: on ? '#0b3e6f' : '#b9c8d6', background: on ? '#0b3e6f' : '#fff' }}
    aria-hidden="true"
  >
    {on ? <CheckIcon size={10} strokeWidth={4} stroke="#fff" /> : null}
  </span>
);

/** One button of a segmented control (`segStyle` in the prototype). */
export const segClasses = (on: boolean): string =>
  `cursor-pointer border-none px-3 py-[7px] text-xs font-semibold ${on ? 'bg-primary text-white' : 'bg-white text-muted'}`;

/** The outlined input of every admin form. */
export const INPUT =
  'rounded border border-control bg-white font-[inherit] text-ink placeholder:text-[#8a9aab] focus:border-primary focus:outline-none';

/** The prototype's small ghost button (`padding:9px 14px`, outlined). */
export const GHOST_BUTTON =
  'cursor-pointer rounded border border-control bg-white px-3.5 py-[9px] text-[12.5px] font-semibold text-muted hover:border-primary hover:text-primary';

/** The primary action that turns grey while it cannot run. */
export const primaryClasses = (enabled: boolean): string =>
  `rounded border-none px-4 py-[9px] text-[12.5px] font-semibold ${
    enabled ? 'cursor-pointer bg-primary text-white hover:bg-primary-deep' : 'cursor-not-allowed bg-control text-faint'
  }`;

/** Closes a popover on a click outside it or on Escape. */
export const useDismiss = (open: boolean, onClose: () => void) => {
  const ref = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) closeRef.current();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeRef.current();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return ref;
};

/** The prototype's modal box for the two big dialogs (item editor, new item, attach). */
export const LARGE_MODAL = 'flex h-[min(760px,calc(100vh-48px))] flex-col';
